import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Device } from '@twilio/voice-sdk';
import { toast } from 'sonner';
import axiosInstance from 'utils/axios';
import { getOzonetelCallStatus } from 'services/integrationService';

const CallContext = createContext();

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

export const CallProvider = ({ children }) => {
  // Call state
  const [callState, setCallState] = useState('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [shouldShowEndModal, setShouldShowEndModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [canShowWidget, setCanShowWidget] = useState(false); // Controls if widget is allowed to show
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callSid, setCallSid] = useState('');
  const [recordingSid, setRecordingSid] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeProvider, setActiveProvider] = useState(null); // 'TWILIO', 'OZONETEL', etc.

  // Refs
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Timer effect for call duration
  useEffect(() => {
    if (callState === 'connected' && callStartTime) {
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [callState, callStartTime]);

  // Setup device only when needed
  const setupDevice = async () => {
    if (deviceRef.current) {
      return deviceRef.current; // Already initialized
    }

    try {
      const res = await axiosInstance.get('/api/Voice/token');

      const { token } = res.data;
      if (!token) {
        throw new Error('No token received from API');
      }

      const device = new Device(token, {
        codecPreferences: ['opus', 'pcmu']
      });

      // device.on('ready', () => {
      //   // // console.info('✅ Global Call Device is ready');
      // });

      device.on('error', err => {
        console.error('❌ Global Call Device error:', err);
        setErrorMessage(`Device error: ${err.message}`);
      });


      device.on('incoming', (call) => {
        console.info('Incoming call from Twilio...');
        call.accept();
        callRef.current = call;
        attachCallListeners(call);
        toast.success('Incoming call accepted');
      });

      deviceRef.current = device;
      await device.register();
      // // console.info('✅ Device registered successfully');

      return device;
    } catch (err) {
      console.error('Failed to initialize global call device:', err);
      setErrorMessage('Failed to initialize voice calling');
      throw err;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, []);

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Control end modal visibility
  const showEndModal = () => {
    if (!isModalOpen) { // Only show if no modal is already open
      setShouldShowEndModal(true);
    }
  };
  const hideEndModal = () => {
    setShouldShowEndModal(false);
    resetCallState();
  };

  // Control modal open state
  const setModalOpen = (open) => setIsModalOpen(open);

  // Control widget visibility
  const setCanShowWidgetState = (canShow) => setCanShowWidget(canShow);

  // Helper: Attach listeners to a connection (outbound or incoming)
  const attachCallListeners = (connection) => {
    // Attach call event handlers
    connection.on('accept', () => {
      // console.info('Global Call Accepted');
      setCallState('connected');
      setCallStartTime(Date.now());
      toast.success('Call connected!', { id: 'call-status' });

      let sid = callRef.current?.parameters?.CallSid;
      if (sid) {
        setCallSid(sid);

        // Auto-start recording when call is connected
        setTimeout(async () => {
          try {
            const response = await axiosInstance.post(`/api/Voice/startRecording?callSid=${sid}`);

            if (response.data.statusCode === 200) {
              const data = JSON.parse(response.data.data);
              setRecordingSid(data.sid);
              setIsRecording(true);
              toast.success('Call recording started automatically');
            }
          } catch (err) {
            console.error("Error auto-starting recording:", err);
          }
        }, 1000);
      }

      // Setup audio elements and devices after connection
      setTimeout(async () => {
        const audioElements = document.querySelectorAll('audio');
        // console.info('Global call - Found audio elements:', audioElements.length);

        // Tag Twilio audio elements for easier detection
        audioElements.forEach(audio => {
          if (audio.srcObject || audio.src) {
            audio.setAttribute('data-twilio', 'true');
            // console.info('🎧 Tagged Twilio audio element');
          }
        });

        // Load available audio devices
        await loadAudioDevices();
      }, 2000);
    });

    connection.on('disconnect', () => {
      // console.info('Global Call Ended');
      setCallState('ended');

      // Only show end modal if no modal is already open
      if (!isModalOpen) {
        setShouldShowEndModal(true);
      }

      toast.success(`Call ended - Duration: ${formatDuration(callDuration)}`, { id: 'call-status' });

      // Stop recording if active
      if (isRecording && recordingSid) {
        stopRecording();
      }

      // Don't auto-reset, let the modal handle it
    });

    connection.on('error', (err) => {
      console.error('Global Call error:', err);
      setCallState('failed');
      setErrorMessage(err.message || 'An error occurred during the call.');
      toast.error('Call failed', { id: 'call-status' });

      if (isRecording && recordingSid) {
        stopRecording();
      }

      setTimeout(() => {
        resetCallState();
      }, 3000);
    });
  };

  // Start a new call (Client-side init - legacy/fallback)
  const initiateCall = async (lead) => {
    try {
      // Initialize device if not already done
      // console.info('📞 Initiating call...');
      const device = await setupDevice();

      if (!device) {
        setErrorMessage("Device not initialized.");
        toast.error("Voice calling not ready");
        return false;
      }

      const leadId = lead?.id || lead?.leadId;
      if (!leadId) {
        setErrorMessage("Lead ID not found.");
        toast.error("Invalid lead data");
        return false;
      }

      setCallState('dialing');
      setLeadData(lead);
      setErrorMessage('');
      setCallDuration(0);
      toast.loading('Dialing...', { id: 'call-status' });

      const options = {
        params: { To: leadId }
      };

      const connection = await device.connect(options);
      callRef.current = connection;
      attachCallListeners(connection);

      return true;
    } catch (error) {
      console.error('Error initiating global call:', error);
      setCallState('failed');
      setErrorMessage('Failed to initiate call');
      toast.error('Failed to initiate call', { id: 'call-status' });
      return false;
    }
  };

  // End the current call
  const endCall = () => {
    if (isRecording && recordingSid) {
      stopRecording();
    }

    if (callRef.current) {
      callRef.current.disconnect();
    } else {
      setCallState('ended');

      // Only show end modal if no modal is already open
      if (!isModalOpen) {
        setShouldShowEndModal(true);
      }

      toast.success(`Call ended - Duration: ${formatDuration(callDuration)}`, { id: 'call-status' });
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (callRef.current) {
      const newMuteState = !isMuted;
      callRef.current.mute(newMuteState);
      setIsMuted(newMuteState);
      toast.info(newMuteState ? 'Microphone muted' : 'Microphone unmuted');
    }
  };

  // Store current audio device info
  const [currentAudioDevice, setCurrentAudioDevice] = useState(null);
  const [availableAudioDevices, setAvailableAudioDevices] = useState([]);

  // Load available audio devices
  const loadAudioDevices = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        // console.info('📱 Audio device enumeration not supported');
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');

      // console.info('🔊 Available audio devices:', audioOutputs);
      setAvailableAudioDevices(audioOutputs);

      // Set default device
      const defaultDevice = audioOutputs.find(device => device.deviceId === 'default') || audioOutputs[0];
      if (defaultDevice) {
        setCurrentAudioDevice(defaultDevice);
      }
    } catch (error) {
      console.error('Failed to load audio devices:', error);
    }
  };

  // Find active audio element
  const findActiveAudioElement = () => {
    // First, try to find Twilio's audio elements
    let audioElements = document.querySelectorAll('audio[data-twilio="true"]');

    if (audioElements.length === 0) {
      // Fallback: find any audio element with active source
      audioElements = document.querySelectorAll('audio');
    }

    for (let audio of audioElements) {
      if (audio.srcObject || audio.src) {
        // console.info('🎵 Found active audio element:', audio);
        return audio;
      }
    }

    // console.info('⚠️ No active audio element found');
    return null;
  };

  // Toggle speaker
  const toggleSpeaker = async () => {
    try {
      if (!callRef.current) {
        toast.error('No active call');
        return;
      }

      const activeAudio = findActiveAudioElement();

      if (!activeAudio) {
        console.error('Audio element not found; skipping speaker toggle until audio is ready');
        return;
      }

      // Check if setSinkId is supported
      if (!activeAudio.setSinkId) {
        toast.info('Audio output switching not supported in this browser');
        return;
      }

      if (!isSpeakerOn) {
        // Switch to speaker/different device
        // console.info('🔊 Switching to speaker mode');

        // Load devices if not already loaded
        if (availableAudioDevices.length === 0) {
          await loadAudioDevices();
        }

        // Try to use the modern selectAudioOutput API first
        if (navigator.mediaDevices.selectAudioOutput) {
          try {
            const device = await navigator.mediaDevices.selectAudioOutput();
            await activeAudio.setSinkId(device.deviceId);
            setCurrentAudioDevice(device);
            toast.success(`Switched to: ${device.label || 'Selected device'}`);
            setIsSpeakerOn(true);
            return;
          } catch (err) {
            console.error('selectAudioOutput failed, trying fallback:', err.message);
          }
        }

        // Fallback: Auto-select a speaker device
        const devices = availableAudioDevices.length > 0 ? availableAudioDevices : await navigator.mediaDevices.enumerateDevices().then(d => d.filter(device => device.kind === 'audiooutput'));

        // Look for a speaker device (prefer non-default devices)
        const speakerDevice = devices.find(device => {
          const label = device.label.toLowerCase();
          return (
            label.includes('speaker') ||
            label.includes('built-in') ||
            label.includes('internal') ||
            (device.deviceId !== 'default' && device.deviceId !== 'communications')
          );
        });

        const targetDevice = speakerDevice || devices.find(d => d.deviceId !== 'default') || devices[0];

        if (targetDevice) {
          await activeAudio.setSinkId(targetDevice.deviceId);
          setCurrentAudioDevice(targetDevice);
          toast.success(`Switched to: ${targetDevice.label || 'Speaker'}`);
          setIsSpeakerOn(true);
        } else {
          toast.info('No alternative audio devices found');
        }

      } else {
        // Switch back to default
        await activeAudio.setSinkId('');
        setCurrentAudioDevice(null);
        toast.success('Switched to default audio output');
        setIsSpeakerOn(false);
      }

    } catch (error) {
      console.error('Speaker toggle error:', error);

      // Provide more specific error messages
      if (error.name === 'NotAllowedError') {
        toast.error('Permission denied. Please allow audio device access.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Audio device not found.');
      } else if (error.message.includes('setSinkId')) {
        toast.info('Audio output switching not supported. Use system settings.');
      } else {
        toast.error(`Audio switching failed: ${error.message}`);
      }

      // Toggle the visual state even if the actual switch failed
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  // Start recording
  const startRecording = async () => {
    if (!callSid) {
      toast.error('No active call to record');
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/Voice/startRecording?callSid=${callSid}`);

      if (response.data.statusCode === 200) {
        const data = JSON.parse(response.data.data);
        setRecordingSid(data.sid);
        setIsRecording(true);
        toast.success('Call recording started');
      } else {
        toast.error('Failed to start recording');
      }
    } catch (err) {
      console.error("Error starting recording:", err);
      toast.error('Failed to start recording');
    }
  };

  // Stop recording
  const stopRecording = async () => {
    if (!recordingSid) {
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/Voice/stopRecording?recordingSid=${recordingSid}`);

      if (response.data.statusCode === 200) {
        setRecordingSid('');
        setIsRecording(false);
        toast.success('Call recording stopped');
      } else {
        toast.error('Failed to stop recording');
      }
    } catch (err) {
      console.error("Error stopping recording:", err);
      toast.error('Failed to stop recording');
    }
  };

  // Reset call state
  const resetCallState = () => {
    setCallState('idle');
    setCallDuration(0);
    setCallStartTime(null);
    setLeadData(null);
    setIsMuted(false);
    setIsSpeakerOn(false);
    setIsRecording(false);
    setCallSid('');
    setRecordingSid('');
    setErrorMessage('');
    setActiveProvider(null);
    setCanShowWidget(false); // Reset widget visibility
    setCurrentAudioDevice(null);
    setAvailableAudioDevices([]);
    callRef.current = null;
    setOzonetelUcid(null);
  };

  /**
   * Triggers the Ozonetel Chrome Extension via postMessage.
   * Extension listens for OZ_DIAL or CLOUD_AGENT_DIAL types.
   */
  const triggerOzonetelExtension = (phoneNumber) => {
    // Clean phone number (remove +, -, spaces, and any non-numeric)
    const cleanNumber = phoneNumber?.replace(/\D/g, '');
    
    if (!cleanNumber) {
      console.warn("Attempted to trigger Ozonetel with empty number");
      return;
    }

    console.info(`Triggering Ozonetel Extension variations for: ${cleanNumber}`);
    
    const messages = [
      { type: 'OZ_DIAL', number: cleanNumber },
      { type: 'CLOUD_AGENT_DIAL', number: cleanNumber },
      { type: 'dial', number: cleanNumber },
      { type: 'click_to_dial', number: cleanNumber },
      { command: 'dial', number: cleanNumber }
    ];

    messages.forEach(msg => {
      window.postMessage(msg, '*');
    });
  };

  // --------------------------------------------------------------------------
  // Ozonetel / External Call Polling Logic (Moved from CallModal)
  // --------------------------------------------------------------------------
  const [ozonetelUcid, setOzonetelUcid] = useState(null);

  useEffect(() => {
    let timeoutId = null;
    let isMounted = true;

    const pollStatus = async () => {
      if (!ozonetelUcid || !isMounted) return;

      try {
        const statusRes = await getOzonetelCallStatus(ozonetelUcid);

        if (isMounted && statusRes.success && statusRes.data?.status && statusRes.data.status !== "unknown") {
          const status = statusRes.data.status;
          const normalizedStatus = status.toLowerCase();

          // Mapping logic
          if (normalizedStatus === "answered" || normalizedStatus === "connected" || normalizedStatus === "calling") {
            setExternalCallStatus('connected');
          } else if (
            normalizedStatus === "disconnect" || 
            normalizedStatus === "completed" || 
            normalizedStatus === "disconnected" ||
            normalizedStatus === "notanswered" ||
            normalizedStatus === "busy" ||
            normalizedStatus === "failed" ||
            normalizedStatus === "cancel" ||
            normalizedStatus === "cancelled"
          ) {
            setExternalCallStatus('ended');
            setOzonetelUcid(null); // Stop polling
            return;
          }
        }
      } catch (error) {
        console.error("Global Polling error:", error);
      }

      // Schedule next poll
      if (isMounted && ozonetelUcid) {
        timeoutId = setTimeout(pollStatus, 3000);
      }
    };

    if (ozonetelUcid) {
      // Start polling
      pollStatus();
    }

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [ozonetelUcid]);

  // Handle external call status updates (e.g., from Ozonetel polling)
  const setExternalCallStatus = (status) => {
    setCallState(status);
    if (status === 'connected') {
      setCallStartTime(prev => prev || Date.now());
    }
  };

  const value = {
    // State
    callState,
    callDuration,
    callStartTime,
    leadData,
    isMuted,
    isSpeakerOn,
    isRecording,
    callSid,
    recordingSid,
    errorMessage,
    shouldShowEndModal,
    isModalOpen,
    canShowWidget,
    currentAudioDevice,
    availableAudioDevices,
    activeProvider,

    // Actions
    initiateCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    startRecording,
    stopRecording,
    resetCallState,
    showEndModal,
    hideEndModal,
    setModalOpen,
    setCanShowWidget: setCanShowWidgetState,
    loadAudioDevices,
    formatDuration,
    connectDevice: setupDevice,
    setActiveProvider,

    // Refs (for advanced usage)
    deviceRef,
    callRef,

    // Manual State Overrides (for Ozonetel/External providers)
    setCallStatus: setExternalCallStatus,
    setLeadData,
    setOzonetelUcid,
    triggerOzonetelExtension
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
};

export default CallContext;
